import React, { useState, useEffect } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { Save, Download, Eye, Edit3, X } from 'lucide-react';

const MarkdownEditor = ({ 
  content, 
  filename, 
  onSave, 
  onClose, 
  readOnly = false,
  showToolbar = true 
}) => {
  const [markdownValue, setMarkdownValue] = useState(content || '');
  const [isEditing, setIsEditing] = useState(!readOnly);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMarkdownValue(content || '');
  }, [content]);

  const handleSave = async () => {
    if (onSave) {
      setSaving(true);
      try {
        await onSave(markdownValue);
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving markdown:', error);
        alert('Failed to save markdown content');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDownload = () => {
    const blob = new Blob([markdownValue], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename || 'document'}.md`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  return (
    <div className="markdown-editor-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 p-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0">
            {isEditing ? <Edit3 size={20} className="me-2" /> : <Eye size={20} className="me-2" />}
            {isEditing ? 'Editing' : 'Viewing'}: {filename}
          </h5>
          <span className="badge bg-secondary">
            {markdownValue.length.toLocaleString()} characters
          </span>
        </div>
        
        <div className="d-flex gap-2">
          {showToolbar && (
            <>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={toggleEditMode}
                title={isEditing ? 'Switch to Preview' : 'Switch to Edit'}
              >
                {isEditing ? <Eye size={16} className="me-1" /> : <Edit3 size={16} className="me-1" />}
                {isEditing ? 'Preview' : 'Edit'}
              </button>
              
              {isEditing && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="me-1" />
                      Save
                    </>
                  )}
                </button>
              )}
              
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleDownload}
                title="Download Markdown"
              >
                <Download size={16} className="me-1" />
                Download
              </button>
            </>
          )}
          
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={onClose}
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Editor/Viewer */}
      <div className="markdown-content-area">
        {isEditing ? (
          <MDEditor
            value={markdownValue}
            onChange={setMarkdownValue}
            height="70vh"
            preview="live"
            className="border rounded"
          />
        ) : (
          <div className="markdown-preview border rounded p-4" style={{ minHeight: '70vh', overflowY: 'auto' }}>
            <MDEditor.Markdown 
              source={markdownValue} 
              style={{ padding: 15 }}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="d-flex justify-content-between align-items-center mt-3 p-3 border-top text-muted">
        <div className="small">
          {isEditing ? 'Edit mode - Use the toolbar above to format your content' : 'Preview mode - Content is read-only'}
        </div>
        <div className="small">
          Last modified: {new Date().toLocaleString()}
        </div>
      </div>

      <style>{`
        .markdown-editor-container {
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .markdown-content-area {
          padding: 0 1rem;
        }
        
        .markdown-preview {
          background: #f8f9fa;
        }
        
        .markdown-preview h1,
        .markdown-preview h2,
        .markdown-preview h3,
        .markdown-preview h4,
        .markdown-preview h5,
        .markdown-preview h6 {
          color: #495057;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
        }
        
        .markdown-preview p {
          line-height: 1.6;
          margin-bottom: 1rem;
        }
        
        .markdown-preview code {
          background: #e9ecef;
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-size: 0.9em;
        }
        
        .markdown-preview pre {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          padding: 1rem;
          overflow-x: auto;
        }
        
        .markdown-preview blockquote {
          border-left: 4px solid #007bff;
          margin: 1rem 0;
          padding-left: 1rem;
          color: #6c757d;
        }
        
        .markdown-preview table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        
        .markdown-preview th,
        .markdown-preview td {
          border: 1px solid #dee2e6;
          padding: 0.5rem;
          text-align: left;
        }
        
        .markdown-preview th {
          background: #f8f9fa;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default MarkdownEditor;

